import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import Header from '../../common/Header/Header';
import {
  Buttons,
  Page,
  TableWrapper,
} from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Divider from '@mui/material/Divider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { showToast } from '../../components/Toast/Toast';
import { useClassSizes, useDeleteClassSize } from '../../services/size/size.service';
import { ClassSize } from '../../services/size/size.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';
import { ValueGetterParams } from 'ag-grid-community';

const ClassSizes: FC = () => {
  const navigate = useNavigate();
  const navigateWithToast = useNavigateWithToast();
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRow, setSelectedRow] = useState<ClassSize | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const muiTheme = createTheme();

  const { data, isError: classSizesError, refetch } = useClassSizes({}, true);
  const allClassSizes = data?.data || [];

  const headers = [
    { label: 'Class Number', key: 'classNo' },
    { label: 'Class Description', key: 'classData_description' },
    { label: 'Class Status Number', key: 'classData_statusNo' },
    { label: 'PCA Class Number', key: 'classData_pcaClassNo' },
    { label: 'Price Option Number', key: 'classData_priceOptionNo' },
    { label: 'Print In Kitchen', key: 'classData_printInKitchen' },
    { label: 'Allow Discount', key: 'classData_allowDiscount' },
    { label: 'Tax Exempted', key: 'classData_taxExempted' },
    { label: 'Allow Half', key: 'classData_allowHalf' },
    { label: 'Class Mnemonic', key: 'classData_mnemonic' },
    { label: 'Class Sequence Number', key: 'classData_seqNo' },
    { label: 'Class Long Description', key: 'classData_longDescription' },
    { label: 'Class Valid On Web', key: 'classData_validOnWeb' },
    { label: 'Class Image ID', key: 'classData_imageId' },
    { label: 'Class Display Description', key: 'classData_displayDesc' },
    { label: 'Size Number', key: 'sizeNo' },
    { label: 'Size Description', key: 'sizeData_description' },
    { label: 'Size Status Number', key: 'sizeData_statusNo' },
    { label: 'Size Mnemonic', key: 'sizeData_mnemonic' },
    { label: 'Size Sequence Number', key: 'sizeData_seqNo' },
    { label: 'Size Long Description', key: 'sizeData_longDescription' },
    { label: 'Size Valid On Web', key: 'sizeData_validOnWeb' },
    { label: 'Display In OE', key: 'sizeData_displayInOe' },
    { label: 'Size Display Description', key: 'sizeData_displayDesc' },
    { label: 'Created By', key: 'createdBy' },
    { label: 'Created Date', key: 'createdDate' },
    { label: 'Updated By', key: 'updatedBy' },
    { label: 'Updated Date', key: 'updatedDate' },
  ];

  const csvData = allClassSizes.map((classSize: ClassSize) => ({
    classNo: classSize.classNo,
    sizeNo: classSize.sizeNo,
    classData_classNo: classSize.classData.classNo,
    classData_description: classSize.classData.description,
    classData_statusNo: classSize.classData.statusNo,
    classData_pcaClassNo: classSize.classData.pcaClassNo,
    classData_priceOptionNo: classSize.classData.priceOptionNo,
    classData_printInKitchen: classSize.classData.printInKitchen ? 'Y' : 'N',
    classData_allowDiscount: classSize.classData.allowDiscount ? 'Y' : 'N',
    classData_taxExempted: classSize.classData.taxExempted ? 'Y' : 'N',
    classData_allowHalf: classSize.classData.allowHalf ? 'Y' : 'N',
    classData_mnemonic: classSize.classData.mnemonic,
    classData_seqNo: classSize.classData.seqNo,
    classData_longDescription: classSize.classData.longDescription,
    classData_validOnWeb: classSize.classData.validOnWeb ? 'Y' : 'N',
    classData_imageId: classSize.classData.imageId,
    classData_displayDesc: classSize.classData.displayDesc,
    sizeData_sizeNo: classSize.sizeData.sizeNo,
    sizeData_description: classSize.sizeData.description,
    sizeData_statusNo: classSize.sizeData.statusNo,
    sizeData_mnemonic: classSize.sizeData.mnemonic,
    sizeData_seqNo: classSize.sizeData.seqNo,
    sizeData_longDescription: classSize.sizeData.longDescription,
    sizeData_validOnWeb: classSize.sizeData.validOnWeb ? 'Y' : 'N',
    sizeData_displayInOe: classSize.sizeData.displayInOe ? 'Y' : 'N',
    sizeData_displayDesc: classSize.sizeData.displayDesc,
    createdBy: classSize.createdBy,
    createdDate: new Date(classSize.createdDate).toISOString(),
    updatedBy: classSize.updatedBy,
    updatedDate: new Date(classSize.updatedDate).toISOString(),
  }));

  const { mutate, isLoading: isDeleteLoading } = useDeleteClassSize();

  const handleRowSelection = () => {
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  };

  const handleAddNew = () => {
    navigate(`/tables/classsizes/addClassSize`);
  };

  const handleDelete = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    mutate(
      {
        classNo: selectedRow?.classNo,
        sizeNo: selectedRow?.sizeNo,
      },
      {
        onSuccess: () => {
          navigateWithToast(`/tables/classsizes`, {
            label: 'ClassSize has been deleted',
            id: 'classSizeDeleteSuccess',
          });
          refetch();
          closeDeleteModal();
          setSelectedRow(undefined);
        },
        onError: () => {
          showToast(genericErrorShowToastOptions);
          closeDeleteModal();
        },
      }
    );
  };

  const openDeleteModal = () => {
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  useFetchErrorToast([{ isError: classSizesError, label: 'Error fetching ClassSizes', id: 'classSizesFetchError' }]);

  const handleOpenClick = () => {
    navigate(`/tables/classsizes/${selectedRow?.classNo}/${selectedRow?.sizeNo}`);
  };

  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='ClassSize' />
      <Page>
        <TableWrapper>
          <Table
            rowData={data?.data}
            ref={gridRef}
            returnedRecords={data?.data?.length}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                maxWidth: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
                flex: 1,
              },
              { minWidth: 80, field: 'classData.classNo', headerName: 'MOA Class Number', flex: 1 },
              { minWidth: 80, field: 'classData.description', headerName: 'MOA Class Description', flex: 1 },
              { minWidth: 112, field: 'sizeNo', headerName: 'MOA Size Number', flex: 1 },
              { minWidth: 80, field: 'sizeData.description', headerName: 'MOA Size Description', flex: 1 },
              {
                minWidth: 80,
                field: 'classData.imageId',
                headerName: 'GUI Image',
                valueFormatter: (params) => (params.value ? 'Y' : 'N'),
                filterValueGetter: (params: ValueGetterParams) => (params.getValue('imageId') ? 'Yes' : 'No'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
          <Button
            label='Open'
            variant={Variant.Primary}
            onClick={handleOpenClick}
            disabled={selectedRow === undefined}
          />
          <Button
            testId='addNewClassSizeActionButton'
            label='Add New'
            variant={Variant.Primary}
            onClick={handleAddNew}
          />
          <Button
            label='Delete'
            testId='openDeleteClassSizeModalConfirmationActionButton'
            variant={Variant.Secondary}
            onClick={openDeleteModal}
            disabled={selectedRow === undefined}
          />
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
          <CSVLink
            data={csvData}
            headers={headers}
            filename={`ClassSizes_${new Date().toISOString().split('T')[0]}.csv`}
            target='_blank'
            className='btn btn-primary'
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Button label='Export to CSV' variant={Variant.Secondary} />
          </CSVLink>
        </Buttons>
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          cancelBtnTestId='cancelDeleteClassSizeModalButton'
          confirmBtnTestId='confirmDeleteClassSizeModalButton'
          title={'Delete ClassSize Record'}
          message={'Are you sure you want to delete this ClassSize?'}
          isLoading={isDeleteLoading}
        />
      </Page>
    </>
  );
};

export default ClassSizes;
